import { z } from "zod";

import type { GoogleOAuthTokens } from "@/hooks/use-google-auth";
import type { ResendEmailResponse, TVerifySchema } from "@/hooks/use-verify-email";
import type { FormSchema, TEmailSchema } from "@/lib/schema";
import type { AuthResponse } from "@/types";

import { emailSchema, userTypeSchema } from "@/lib/schema";
import env from "@/valid-env";

import axios from "./axios";

export async function resendEmailVerification(data: TEmailSchema, endpoint: string): Promise<ResendEmailResponse> {
	const { data: validData, error } = emailSchema.safeParse(data);
	if (error)
		throw new Error(error.errors[0].message);
	const response = await axios.post(`/auth/${endpoint}`, validData);

	return response.data;
}
export async function verifyEmail(data: TVerifySchema) {
	const response = await axios.post("/auth/verify_email", data);

	return response.data as AuthResponse;
}

export async function authenticateUser(_data: FormSchema, type: "sign-in" | "sign-up") {
	// @ts-expect-error tos is not in the type
	const { tos, confirmPassword, ...data } = _data;
	const url = `/auth/${type === "sign-up" ? "create_user" : "login"}`;

	let mainData: any;
	if (type === "sign-up") {
		// Type assertion: data as sign-up form
		mainData = {
			firstName: (data as any).firstName,
			lastName: (data as any).lastName,
			email: data.email,
			username: (data as any).username,
			referrerCode: (data as any).referrerCode || undefined,
			password: data.password,
			phoneNumber: (data as any).phoneNumber,
			userType: data.userType,
			roles: data.userType,
			language: "English"
		};
	} else {
		// Type assertion: data as sign-in form
		mainData = {
			email: data.email,
			password: data.password,
			userType: data.userType
		};
	}

	const response = await axios.post(url, mainData);

	return response.data as AuthResponse;
}

export const GoogleSignInSchema = z.object({
	token: z.string().min(1),
	userType: userTypeSchema.optional(),
	roles: userTypeSchema.optional(),
	countryId: z.string().optional(),
	language: z.string().optional()
});

export type TGoogleSignInSchema = z.infer<typeof GoogleSignInSchema>;

export async function googleSign(data: z.infer<typeof GoogleSignInSchema>) {
	const { data: validData, error } = GoogleSignInSchema.safeParse(data);
	if (error)
		throw new Error(error.errors[0].message);

	const response = await axios.post("/auth/google-signin-signup", validData);

	return response.data as AuthResponse;
}
export async function oAuthSignIn(clientId: string, redirectUri: string) {
	const params = new URLSearchParams({
		client_id: clientId,
		redirect_uri: redirectUri,
		response_type: "token",
		scope: "https://www.googleapis.com/auth/drive.metadata.readonly",
		include_granted_scopes: "true",
		state: "pass-through value"
	});
	const url = `/auth/google-signin-signup${params}`;

	const response = await axios.get(url);

	return response.data as AuthResponse;
}

export async function getGoogleOAuthTokens({ code, redirect_uri }: { code: string; redirect_uri: string }): Promise<GoogleOAuthTokens> {
	const url = "https://oauth2.googleapis.com/token";

	const values = {
		code,
		redirect_uri,
		client_id: env.VITE_GOOGLE_CLIENT_ID,
		client_secret: env.VITE_GOOGLE_CLIENT_SECRET,
		grant_type: "authorization_code"
	};

	try {
		const res = await axios.post(
			url,
			new URLSearchParams(values).toString(),
			{
				headers: {
					"Content-Type": "application/x-www-form-urlencoded"
				}
			}
		);
		return res.data;
	}
	catch (error) {
		console.error("Failed to fetch Google OAuth tokens", error);
		throw error;
	}
}
